import { ElementType, useEffect, useRef, useState } from "react";
import { styled, keyframes } from "@mui/material";
import { Box } from "@mui/system";
import { MessageView } from "@my-helpers/common";
import { ThreadMessage } from "./Message";

const fadeOutSlideUp = keyframes`
    0% {
        opacity: 1;
        transform: translateY(0);
        maxHeight: 1000px;
    }
    50% {
        opacity: 0;
        transform: translateY(-20px);
    }
    100% {
        opacity: 0;
        transform: translateY(-40px);
        maxHeight: 0;
        margin: 0;
        padding: 0;
    }
`;

const fadeInSlideDown = keyframes`
    0% {
        opacity: 0;
        transform: translateY(-10px);
    }
    100% {
        opacity: 1;
        transform: translateY(0);
    }
`;

export const MessagesArea = styled(Box)(({ theme }) => ({
    flex: 1,
    backgroundColor: theme.palette.background.default,
    overflowY: 'auto',
    padding: theme.spacing(1, 2),
    '&::-webkit-scrollbar': {
        width: '8px',
    },
    '&::-webkit-scrollbar-track': {
        backgroundColor: theme.palette.grey[100],
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.palette.grey[400],
        borderRadius: '4px',
    },
}));

const MessageWrapper = styled(Box)<{ isExiting?: boolean; isEntering?: boolean }>(({ isExiting, isEntering }) => ({
    animation: isExiting 
        ? `${fadeOutSlideUp} 0.4s ease-out forwards`
        : isEntering
        ? `${fadeInSlideDown} 0.3s ease-out`
        : 'none',
}));

interface MessagesProps {
    isLoading?: string;
    error?: string;
    messages: MessageView[];
    expandedThinkBlocks: Set<string>;
    refreshMessage: (messageId: string) => Promise<void>
    setExpandedThinkBlocks: React.Dispatch<React.SetStateAction<Set<string>>>
    getResponses: (messageId: string) => Promise<void>
}

const Messages: ElementType<MessagesProps> = 
({ 
    isLoading, 
    error,
    messages,
    expandedThinkBlocks, 
    refreshMessage,
    setExpandedThinkBlocks,
    getResponses
}) => {

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const [displayMessages, setDisplayMessages] = useState<MessageView[]>(messages);
    const [exitingMessageIds, setExitingMessageIds] = useState<Set<string>>(new Set());
    const [enteringMessageIds, setEnteringMessageIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, messages.length]);

    useEffect(() => {
        const currentMessageIds = new Set(messages.map(m => m.id));
        const displayMessageIds = new Set(displayMessages.map(m => m.id));
        
        // Find messages that are being removed
        const removedIds = new Set(
            [...displayMessageIds].filter(id => !currentMessageIds.has(id))
        );
        
        // Find messages that are being added
        const addedIds = new Set(
            [...currentMessageIds].filter(id => !displayMessageIds.has(id))
        );

        if (removedIds.size > 0) {
            // Start exit animation for removed messages
            setExitingMessageIds(removedIds);
            
            // After animation completes, update display messages
            setTimeout(() => {
                setDisplayMessages(messages);
                setExitingMessageIds(new Set());
                
                // Trigger enter animation for new messages
                if (addedIds.size > 0) {
                    setEnteringMessageIds(addedIds);
                    setTimeout(() => {
                        setEnteringMessageIds(new Set());
                    }, 300);
                }
            }, 400);
        } else if (addedIds.size > 0) {
            // Only new messages, no removals
            setDisplayMessages(messages);
            setEnteringMessageIds(addedIds);
            setTimeout(() => {
                setEnteringMessageIds(new Set());
            }, 300);
        } else {
            // Messages updated in place
            setDisplayMessages(messages);
        }
    }, [messages]);

    const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (isLoading || error || !messages || messages.length === 0) return;

        // Clear any existing timeout
        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
        }

        // Only check the last message
        const lastMessage = messages[messages.length - 1];
        
        // If last message is pending or working, refresh it
        if (lastMessage.status === 'pending' || lastMessage.status === 'working') {
            refreshTimeoutRef.current = setTimeout(() => {
                refreshMessage(lastMessage.id);
            }, 6000);
        }
        // Otherwise, if last message is a user message with 'sent' status, get responses
        else if (lastMessage.sender.role === 'user' && lastMessage.status === 'sent') {
            refreshTimeoutRef.current = setTimeout(() => {
                getResponses(lastMessage.id);
            }, 6000);
        }

        // Cleanup timeout on unmount
        return () => {
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }
        };
    }, [isLoading, error, messages, refreshMessage, getResponses]);

    const toggleThinkBlock = (blockId: string) => {
        setExpandedThinkBlocks(prev => {
            const newSet = new Set(prev);
            if (newSet.has(blockId)) {
                newSet.delete(blockId);
            } else {
                newSet.add(blockId);
            }
            return newSet;
        });
    };

    return (
        <MessagesArea>
            {
                displayMessages.map((message: MessageView) => {
                    const isExiting = exitingMessageIds.has(message.id);
                    const isEntering = enteringMessageIds.has(message.id);
                    
                    return (
                        <MessageWrapper 
                            key={message.id}
                            isExiting={isExiting}
                            isEntering={isEntering}
                        >
                            <ThreadMessage
                                expandedThinkBlocks={expandedThinkBlocks}
                                toggleThinkBlock={toggleThinkBlock}
                                message={message}
                            />
                        </MessageWrapper>
                    );
                })
            }

            <div ref={messagesEndRef} />
        </MessagesArea>
    )
}

export default Messages